"use client"

/**
 * Testimonials Carousel
 * 
 * Drag/swipe pattern: Disable CSS snap during drag → animate to nearest on release → re-enable snap.
 * See design-animations.md for carousel implementation details.
 */
import { useRef, useEffect, useCallback, useState, type ReactNode } from "react"
import Image from "next/image"
import { T } from "gt-next"
import { TESTIMONIALS } from "../constants/navigation-landing"

export interface Testimonial {
    id: number
    quote: string
    author: string
    role: string
    avatar?: string
    logo?: string
    logoWidth?: number
    bgColor?: string
}

interface TestimonialsProps {
    testimonials?: Testimonial[]
    eyebrow?: ReactNode
    title?: ReactNode
    autoPlay?: boolean
    autoPlayInterval?: number
}

// Static <T> lookups for the testimonial data (src/utils/clients.ts stays unchanged).
// Keys are the exact English quote / role strings; unknown strings render as-is.
const QUOTE_T: Record<string, ReactNode> = {
    "Went from 8k to 127k in 6 months. My brand deals tripled.": <T>Went from 8k to 127k in 6 months. My brand deals tripled.</T>,
    "156K followers and getting paid to travel. This changed my life.": <T>156K followers and getting paid to travel. This changed my life.</T>,
    "My art is finally reaching people who actually appreciate it.": <T>My art is finally reaching people who actually appreciate it.</T>,
    "6.8K to 92K in under 7 months. Makeup brands now reach out to me.": <T>6.8K to 92K in under 7 months. Makeup brands now reach out to me.</T>,
    "Grew my consulting business to 6 figures thanks to my Instagram growth.": <T>Grew my consulting business to 6 figures thanks to my Instagram growth.</T>
}

const ROLE_T: Record<string, ReactNode> = {
    "Art Creator": <T>Art Creator</T>,
    "Beauty Creator": <T>Beauty Creator</T>,
    "Business Creator": <T>Business Creator</T>,
    "Fashion Creator": <T>Fashion Creator</T>,
    "Fitness Creator": <T>Fitness Creator</T>,
    "Food Creator": <T>Food Creator</T>,
    "Lifestyle Creator": <T>Lifestyle Creator</T>,
    "Music Creator": <T>Music Creator</T>,
    "Photography Creator": <T>Photography Creator</T>,
    "Tech Creator": <T>Tech Creator</T>,
    "Travel Creator": <T>Travel Creator</T>,
    "Wellness Creator": <T>Wellness Creator</T>
}

// Constants
const GAP = 24
const MOBILE_PADDING = 16

export default function Testimonials({ testimonials = TESTIMONIALS, eyebrow = <T>Testimonials</T>, title = <T>1,900+ brands grow with Outfame</T>, autoPlay = true, autoPlayInterval = 5000 }: TestimonialsProps) {
    const [currentIndex, setCurrentIndex] = useState(0)
    const [isPaused, setIsPaused] = useState(false)
    const trackRef = useRef<HTMLDivElement>(null)
    const autoPlayRef = useRef<NodeJS.Timeout | null>(null)
    // Gates scroll events during programmatic navigation to prevent index race conditions
    const isNavigatingRef = useRef(false)
    const dragStateRef = useRef({
        isDragging: false,
        startX: 0,
        scrollLeft: 0,
        dragDistance: 0,
        startTime: 0,
        startIndex: 0
    })

    const totalSlides = testimonials.length

    // Get scroll position for a given slide index
    const getScrollPositionForIndex = useCallback((index: number): number => {
        if (!trackRef.current) return 0
        
        const slides = trackRef.current.querySelectorAll("[data-slide]")
        if (!slides[index]) return 0
        
        const slide = slides[index] as HTMLElement
        const container = trackRef.current
        
        // Center the slide in the viewport
        const containerWidth = container.clientWidth
        const slideWidth = slide.offsetWidth
        const slideLeft = slide.offsetLeft
        
        return slideLeft - (containerWidth - slideWidth) / 2
    }, [])

    // Navigate to specific slide
    const goTo = useCallback((index: number) => {
        const newIndex = Math.max(0, Math.min(index, totalSlides - 1))
        if (!trackRef.current) return
        
        isNavigatingRef.current = true
        setCurrentIndex(newIndex)
        
        const scrollPosition = getScrollPositionForIndex(newIndex)
        trackRef.current.scrollTo({ left: scrollPosition, behavior: "smooth" })

        // Reset navigating flag after animation
        setTimeout(() => {
            isNavigatingRef.current = false
        }, 500)
    }, [totalSlides, getScrollPositionForIndex])

    const next = useCallback(() => {
        goTo(currentIndex < totalSlides - 1 ? currentIndex + 1 : 0)
    }, [currentIndex, totalSlides, goTo])

    const prev = useCallback(() => {
        goTo(currentIndex > 0 ? currentIndex - 1 : totalSlides - 1)
    }, [currentIndex, totalSlides, goTo])

    // Detect which slide is most visible and update currentIndex
    const detectCurrentSlide = useCallback(() => {
        if (!trackRef.current || isNavigatingRef.current) return
        
        const container = trackRef.current
        const slides = container.querySelectorAll("[data-slide]")
        const containerRect = container.getBoundingClientRect()
        const containerCenter = containerRect.left + containerRect.width / 2
        
        let closestIndex = 0
        let closestDistance = Infinity
        
        slides.forEach((slide, index) => {
            const slideRect = slide.getBoundingClientRect()
            const slideCenter = slideRect.left + slideRect.width / 2
            const distance = Math.abs(containerCenter - slideCenter)
            
            if (distance < closestDistance) {
                closestDistance = distance
                closestIndex = index
            }
        })
        
        if (closestIndex !== currentIndex) {
            setCurrentIndex(closestIndex)
        }
    }, [currentIndex])

    // Handle scroll event
    const handleScroll = useCallback(() => {
        if (isNavigatingRef.current || dragStateRef.current.isDragging) return
        detectCurrentSlide()
    }, [detectCurrentSlide])

    // Animate to target slide after drag ends, then re-enable snap
    // Uses velocity + direction-aware logic for natural swipe feel
    const animateToTargetSlide = useCallback((deltaX: number, elapsedMs: number) => {
        if (!trackRef.current || isNavigatingRef.current) return
        
        const container = trackRef.current
        const slides = container.querySelectorAll("[data-slide]")
        if (!slides.length) return
        
        const slideWidth = (slides[0] as HTMLElement).offsetWidth + GAP
        const startIdx = dragStateRef.current.startIndex
        
        // Calculate velocity (px/ms) - positive = swiping left (next), negative = swiping right (prev)
        const velocity = elapsedMs > 0 ? -deltaX / elapsedMs : 0
        const velocityThreshold = 0.3 // px/ms - fast flick threshold
        
        // Calculate swipe distance as percentage of slide width
        const swipePercent = Math.abs(deltaX) / slideWidth
        const swipeThreshold = 0.25 // 25% of slide width
        
        let targetIndex = startIdx
        
        // Determine target based on velocity OR distance
        if (Math.abs(velocity) > velocityThreshold) {
            // Fast flick - go in swipe direction regardless of distance
            targetIndex = velocity > 0 ? startIdx + 1 : startIdx - 1
        } else if (swipePercent > swipeThreshold) {
            // Slow swipe but past threshold - go in swipe direction
            targetIndex = deltaX < 0 ? startIdx + 1 : startIdx - 1
        }
        // Else: snap back to start slide (targetIndex stays as startIdx)
        
        // Clamp to valid range
        targetIndex = Math.max(0, Math.min(targetIndex, totalSlides - 1))
        
        // Animate to target
        const scrollPosition = getScrollPositionForIndex(targetIndex)
        container.scrollTo({ left: scrollPosition, behavior: "smooth" })
        setCurrentIndex(targetIndex)
        
        // Re-enable snap after smooth scroll completes (~300ms browser default)
        setTimeout(() => {
            if (trackRef.current) {
                trackRef.current.style.scrollSnapType = ""
            }
        }, 350)
    }, [getScrollPositionForIndex, totalSlides])

    // Mouse drag handlers
    const handleMouseDown = useCallback((e: React.MouseEvent) => {
        if (!trackRef.current) return
        e.preventDefault()
        
        dragStateRef.current = {
            isDragging: true,
            startX: e.clientX,
            scrollLeft: trackRef.current.scrollLeft,
            dragDistance: 0,
            startTime: Date.now(),
            startIndex: currentIndex
        }
        
        trackRef.current.style.cursor = "grabbing"
        trackRef.current.style.scrollBehavior = "auto"
        // Disable snap during drag for smooth 1:1 cursor tracking
        trackRef.current.style.scrollSnapType = "none"
    }, [currentIndex])

    const handleMouseMove = useCallback((e: MouseEvent) => {
        if (!dragStateRef.current.isDragging || !trackRef.current) return
        
        const deltaX = e.clientX - dragStateRef.current.startX
        dragStateRef.current.dragDistance = deltaX // Keep signed for direction
        trackRef.current.scrollLeft = dragStateRef.current.scrollLeft - deltaX
    }, [])

    const handleMouseUp = useCallback(() => {
        if (!trackRef.current) return
        
        const wasDragging = dragStateRef.current.isDragging
        const deltaX = dragStateRef.current.dragDistance
        const elapsedMs = Date.now() - dragStateRef.current.startTime
        
        dragStateRef.current.isDragging = false
        trackRef.current.style.cursor = "grab"
        trackRef.current.style.scrollBehavior = ""
        
        // >10px = intentional drag → animate to target based on velocity/direction
        // <10px = click → re-enable snap immediately (browser handles naturally)
        if (wasDragging && Math.abs(deltaX) > 10) {
            animateToTargetSlide(deltaX, elapsedMs)
        } else {
            trackRef.current.style.scrollSnapType = ""
        }
    }, [animateToTargetSlide])

    // Attach global mouse listeners
    useEffect(() => {
        document.addEventListener("mousemove", handleMouseMove)
        document.addEventListener("mouseup", handleMouseUp)
        return () => {
            document.removeEventListener("mousemove", handleMouseMove)
            document.removeEventListener("mouseup", handleMouseUp)
        }
    }, [handleMouseMove, handleMouseUp])

    // Touch handlers
    const handleTouchStart = useCallback((e: React.TouchEvent) => {
        if (!trackRef.current) return
        
        // Pause auto-play immediately on touch
        setIsPaused(true)
        
        dragStateRef.current = {
            isDragging: true,
            startX: e.touches[0].clientX,
            scrollLeft: trackRef.current.scrollLeft,
            dragDistance: 0,
            startTime: Date.now(),
            startIndex: currentIndex
        }
        trackRef.current.style.scrollBehavior = "auto"
        trackRef.current.style.scrollSnapType = "none"
    }, [currentIndex])

    const handleTouchMove = useCallback((e: React.TouchEvent) => {
        if (!dragStateRef.current.isDragging || !trackRef.current) return
        
        const deltaX = e.touches[0].clientX - dragStateRef.current.startX
        dragStateRef.current.dragDistance = deltaX // Keep signed for direction
        trackRef.current.scrollLeft = dragStateRef.current.scrollLeft - deltaX
    }, [])

    const handleTouchEnd = useCallback(() => {
        if (!trackRef.current) return
        
        const deltaX = dragStateRef.current.dragDistance
        const elapsedMs = Date.now() - dragStateRef.current.startTime
        
        dragStateRef.current.isDragging = false
        trackRef.current.style.scrollBehavior = ""
        
        if (Math.abs(deltaX) > 10) {
            animateToTargetSlide(deltaX, elapsedMs)
        } else {
            trackRef.current.style.scrollSnapType = ""
        }
        
        // Resume auto-play after brief delay (allows animation to complete)
        setTimeout(() => setIsPaused(false), 500)
    }, [animateToTargetSlide])

    // Handle scroll end detection
    useEffect(() => {
        const container = trackRef.current
        if (!container) return
        
        let scrollEndTimer: NodeJS.Timeout
        
        const handleScrollEnd = () => {
            clearTimeout(scrollEndTimer)
            scrollEndTimer = setTimeout(() => {
                if (!isNavigatingRef.current && !dragStateRef.current.isDragging) {
                    detectCurrentSlide()
                }
            }, 100)
        }
        
        container.addEventListener("scroll", handleScrollEnd)
        return () => {
            container.removeEventListener("scroll", handleScrollEnd)
            clearTimeout(scrollEndTimer)
        }
    }, [detectCurrentSlide])

    // Auto-play logic
    useEffect(() => {
        if (autoPlay && !isPaused) {
            autoPlayRef.current = setInterval(next, autoPlayInterval)
        }
        return () => {
            if (autoPlayRef.current) clearInterval(autoPlayRef.current)
        }
    }, [autoPlay, autoPlayInterval, isPaused, next])

    const pause = useCallback(() => setIsPaused(true), [])
    const resume = useCallback(() => setIsPaused(false), [])

    return (
        <section className="bg-white" aria-labelledby="testimonials-heading">
            <div className="w-full">
                <div className="flex flex-col gap-4 bg-white pt-20 md:gap-8 md:pt-40">
                    {/* Section Header */}
                    <div className="flex flex-col items-center gap-0 px-3 text-center">
                        <p className="mb-4 text-[14px] font-semibold uppercase leading-[20px] tracking-widest" style={{ color: "rgba(20, 20, 20, 0.5)" }}>{eyebrow}</p>
                        <h2 id="testimonials-heading" className="text-[28px] font-semibold leading-[34px] text-outfame-black md:text-[35px] md:leading-[40.25px]">
                            {title}
                        </h2>
                    </div>

                    {/* Navigation Arrows */}
                    <div className="flex justify-center gap-2">
                        <button onClick={prev} className="flex h-10 w-10 items-center justify-center rounded-button bg-black transition-opacity hover:opacity-90" aria-label="Previous testimonial">
                            <svg className="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                        </button>
                        <button onClick={next} className="flex h-10 w-10 items-center justify-center rounded-button bg-black transition-opacity hover:opacity-90" aria-label="Next testimonial">
                            <svg className="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                        </button>
                    </div>

                    {/* Testimonials Carousel */}
                    <div className="relative" onMouseEnter={pause} onMouseLeave={resume}>
                        <div
                            ref={trackRef}
                            onScroll={handleScroll}
                            onMouseDown={handleMouseDown}
                            onTouchStart={handleTouchStart}
                            onTouchMove={handleTouchMove}
                            onTouchEnd={handleTouchEnd}
                            className="no-scrollbar scroll-fade-x flex cursor-grab snap-x snap-mandatory overflow-x-auto scroll-smooth select-none active:cursor-grabbing"
                            style={{
                                gap: `${GAP}px`,
                                paddingLeft: `max(${MOBILE_PADDING}px, calc(50vw - 568px))`,
                                paddingRight: `max(${MOBILE_PADDING}px, calc(50vw - 568px))`,
                                scrollPaddingLeft: `max(${MOBILE_PADDING}px, calc(50vw - 568px))`,
                                scrollPaddingRight: `max(${MOBILE_PADDING}px, calc(50vw - 568px))`,
                                WebkitOverflowScrolling: "touch"
                            }}>
                            {testimonials.map((testimonial) => (
                                <div 
                                    key={testimonial.id} 
                                    className="w-[calc(100vw-2rem)] shrink-0 snap-center md:w-[556px]" 
                                    data-slide
                                >
                                    <div
                                        className="flex h-full min-h-[350px] flex-col justify-between rounded-card md:min-h-[406px]"
                                        style={{
                                            backgroundColor: testimonial.bgColor || "rgb(254, 233, 221)",
                                            padding: "40px 32px 48px"
                                        }}>
                                        <div>
                                            {testimonial.logo && <Image src={testimonial.logo} alt={`${testimonial.author}'s company logo`} width={testimonial.logoWidth || 100} height={40} className="mb-5" />}
                                            <blockquote className="text-[18px] font-normal leading-[28px] text-black md:text-[20px]">"{QUOTE_T[testimonial.quote] ?? testimonial.quote}"</blockquote>
                                        </div>
                                        <footer className="mt-8 flex items-end gap-4">
                                            {testimonial.avatar && <Image src={testimonial.avatar} alt={testimonial.author} width={56} height={56} className="img-outline rounded-full object-cover" />}
                                            <div>
                                                <cite className="block text-[16px] font-semibold not-italic leading-[24px] text-black">{testimonial.author}</cite>
                                                <p className="text-[14px] leading-[18px] text-black/75">{ROLE_T[testimonial.role] ?? testimonial.role}</p>
                                            </div>
                                        </footer>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Dots */}
                    <div className="mx-auto mt-4 flex w-full max-w-container justify-center px-4" style={{ gap: "16px" }}>
                        {testimonials.map((testimonial, index) => (
                            <button
                                key={testimonial.id}
                                onClick={() => goTo(index)}
                                className="h-2 flex-1 rounded-sm transition-colors"
                                style={{
                                    backgroundColor: currentIndex === index ? "#141414" : "#efefee"
                                }}
                                aria-label={`Go to testimonial ${index + 1}`}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </section>
    )
}
